import { useEffect, useRef, useState } from 'react'
import { getRemoteMediaBlob, uploadMediaDirect } from '../api'
import { getMedia, mediaUrl, storeMedia } from '../offline'
import { makeId, nowIso } from '../model'
import type { Evidence, EvidenceKind, Observation, VisitSnapshot } from '../types'

interface Props {
  visit: VisitSnapshot
  observation: Observation
  commit: (mutator: (visit: VisitSnapshot) => VisitSnapshot) => Promise<void>
  storageMode: 'device' | 'cloud' | 'unavailable'
}

async function addEvidence(
  visit: VisitSnapshot,
  observation: Observation,
  blob: Blob,
  kind: EvidenceKind,
  fileName: string,
  commit: Props['commit'],
  storageMode: Props['storageMode'],
) {
  const id = makeId()
  const uploaded = storageMode === 'cloud'
    ? await uploadMediaDirect({ visitId: visit.id, evidenceId: id, fileName, blob })
    : null
  if (storageMode === 'device') await storeMedia(id, visit.id, blob)
  const evidence: Evidence = {
    id,
    observationId: observation.id,
    kind,
    fileName,
    mimeType: blob.type,
    caption: '',
    transcript: '',
    localOnly: storageMode === 'device',
    objectKey: uploaded?.objectKey,
    createdAt: nowIso(),
    createdBy: visit.contributorName,
  }
  await commit((current) => ({
    ...current,
    evidence: [...current.evidence, evidence],
    observations: current.observations.map((item) => (
      item.id === observation.id
        ? { ...item, evidenceIds: [...item.evidenceIds, id], updatedAt: nowIso() }
        : item
    )),
  }))
}

function EvidenceThumb({
  evidence,
  onCaption,
  onTranscript,
  onAnnotate,
  onDelete,
  storageMode,
}: {
  evidence: Evidence
  onCaption: (caption: string) => void
  onTranscript: (transcript: string) => void
  onAnnotate?: () => void
  onDelete: () => void
  storageMode: Props['storageMode']
}) {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    let blobUrl: string | null = null
    void (storageMode === 'device' ? mediaUrl(evidence.id) : Promise.resolve(null)).then(async (local) => {
      blobUrl = local ?? (!evidence.localOnly ? URL.createObjectURL(await getRemoteMediaBlob(evidence.id)) : null)
      const next = blobUrl
      if (active) setUrl(next)
      else if (next) URL.revokeObjectURL(next)
    }).catch(() => {
      if (active) setUrl(null)
    })
    return () => {
      active = false
      if (blobUrl) URL.revokeObjectURL(blobUrl)
    }
  }, [evidence.id, evidence.localOnly, storageMode])

  return (
    <article className="evidence-thumb">
      <div className="evidence-preview">
        {evidence.kind === 'photo' || evidence.kind === 'sketch' ? (
          url ? <img src={url} alt={evidence.caption || evidence.fileName} /> : <span>Loading…</span>
        ) : evidence.kind === 'audio' ? (
          url ? <audio controls src={url} /> : <span>Loading audio…</span>
        ) : (
          <span>Document</span>
        )}
        <span className={`media-badge ${evidence.localOnly ? 'media-badge--local' : ''}`}>
          {evidence.localOnly ? 'On device' : 'Uploaded'}
        </span>
      </div>
      <details className="evidence-details">
        <summary>{evidence.caption || 'Add caption or manage'}</summary>
        <div className="evidence-details__body">
          <label>
            Caption
            <input
              value={evidence.caption}
              onChange={(event) => onCaption(event.target.value)}
              placeholder="What does this show?"
            />
          </label>
          {evidence.kind === 'audio' && (
            <label>
              Editable transcript or summary
              <textarea
                rows={3}
                value={evidence.transcript ?? ''}
                onChange={(event) => onTranscript(event.target.value)}
                placeholder="Transcription can be added or corrected later."
              />
            </label>
          )}
          <div className="evidence-actions">
            {onAnnotate && (
              <button type="button" className="text-button" onClick={onAnnotate}>
                Mark up this photo
              </button>
            )}
            <button type="button" className="text-button text-button--danger" onClick={onDelete}>
              Remove evidence
            </button>
          </div>
        </div>
      </details>
    </article>
  )
}

function SketchPad({
  backgroundId,
  onSave,
  onCancel,
  storageMode,
}: {
  backgroundId?: string
  onSave: (blob: Blob) => void
  onCancel: () => void
  storageMode: Props['storageMode']
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const [ready, setReady] = useState(!backgroundId)

  useEffect(() => {
    if (!backgroundId) return
    let objectUrl = ''
    void (storageMode === 'device' ? getMedia(backgroundId) : Promise.resolve(undefined)).then(async (item) => {
      let blob = item?.blob
      if (!blob) {
        blob = await getRemoteMediaBlob(backgroundId)
      }
      if (!blob || !canvasRef.current) return
      objectUrl = URL.createObjectURL(blob)
      const image = new Image()
      image.onload = () => {
        const canvas = canvasRef.current
        if (!canvas) return
        const context = canvas.getContext('2d')
        if (!context) return
        context.fillStyle = '#fff'
        context.fillRect(0, 0, canvas.width, canvas.height)
        const ratio = Math.min(canvas.width / image.width, canvas.height / image.height)
        const width = image.width * ratio
        const height = image.height * ratio
        context.drawImage(image, (canvas.width - width) / 2, (canvas.height - height) / 2, width, height)
        setReady(true)
      }
      image.src = objectUrl
    })
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [backgroundId, storageMode])

  const point = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = event.currentTarget
    const rect = canvas.getBoundingClientRect()
    return {
      x: ((event.clientX - rect.left) / rect.width) * canvas.width,
      y: ((event.clientY - rect.top) / rect.height) * canvas.height,
    }
  }

  const start = (event: React.PointerEvent<HTMLCanvasElement>) => {
    drawing.current = true
    event.currentTarget.setPointerCapture(event.pointerId)
    const context = event.currentTarget.getContext('2d')
    const current = point(event)
    context?.beginPath()
    context?.moveTo(current.x, current.y)
  }

  const move = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return
    const context = event.currentTarget.getContext('2d')
    if (!context) return
    const current = point(event)
    context.strokeStyle = '#e63946'
    context.lineWidth = 5
    context.lineCap = 'round'
    context.lineJoin = 'round'
    context.lineTo(current.x, current.y)
    context.stroke()
  }

  const save = () => {
    canvasRef.current?.toBlob((blob) => {
      if (blob) onSave(blob)
    }, 'image/png')
  }

  return (
    <div className="drawing-shell">
      <div className="drawing-heading">
        <strong>{backgroundId ? 'Mark up photo' : 'Draw a note'}</strong>
        <span>Use a finger or Apple Pencil. Red marks are saved as a new image.</span>
      </div>
      <canvas
        ref={canvasRef}
        width="1200"
        height="800"
        aria-label="Drawing canvas"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={() => { drawing.current = false }}
        onPointerCancel={() => { drawing.current = false }}
      />
      <div className="button-row">
        <button className="button button--secondary" type="button" onClick={onCancel}>Cancel</button>
        <button className="button" type="button" onClick={save} disabled={!ready}>Save drawing</button>
      </div>
    </div>
  )
}

export default function MediaCapture({ visit, observation, commit, storageMode }: Props) {
  const [recording, setRecording] = useState(false)
  const [recordingSeconds, setRecordingSeconds] = useState(0)
  const [drawingMode, setDrawingMode] = useState<
    { type: 'sketch' } | { type: 'annotate'; evidenceId: string } | null
  >(null)
  const [mediaError, setMediaError] = useState('')
  const [mediaStatus, setMediaStatus] = useState('')
  const [saving, setSaving] = useState(false)
  const recorder = useRef<MediaRecorder | null>(null)
  const stream = useRef<MediaStream | null>(null)
  const chunks = useRef<Blob[]>([])
  const cameraInput = useRef<HTMLInputElement>(null)
  const mounted = useRef(true)
  const evidence = visit.evidence.filter((item) => observation.evidenceIds.includes(item.id) && !item.deletedAt)
  const removedEvidence = visit.evidence.filter((item) => item.observationId === observation.id && item.deletedAt)
  const photoCount = evidence.filter((item) => item.kind === 'photo').length
  const otherCount = evidence.length - photoCount

  const receiveFiles = async (files: FileList | null, kind: EvidenceKind) => {
    const selected = Array.from(files ?? [])
    if (!selected.length) return
    setMediaError('')
    setSaving(true)
    const noun = kind === 'photo' ? 'photo' : 'file'
    try {
      for (const [index, file] of selected.entries()) {
        setMediaStatus(`Saving ${index + 1} of ${selected.length} ${noun}${selected.length === 1 ? '' : 's'}…`)
        if (file.size > 100 * 1024 * 1024) throw new Error(`${file.name} is larger than the 100 MB limit.`)
        if (storageMode === 'device') {
          const estimate = await navigator.storage?.estimate?.()
          if (estimate?.quota && estimate.usage && file.size > estimate.quota - estimate.usage) {
            throw new Error('This device does not have enough browser storage for that file.')
          }
        }
        if (storageMode === 'cloud' && !navigator.onLine) {
          throw new Error('Reconnect before adding media in cloud-save mode. Your typed notes remain saved.')
        }
        await addEvidence(visit, observation, file, kind, file.name, commit, storageMode)
      }
      setMediaStatus(`${storageMode === 'cloud' ? 'Uploaded' : 'Saved on device'} · ${selected.length} ${noun}${selected.length === 1 ? '' : 's'}`)
    } catch (error) {
      setMediaError(error instanceof Error ? error.message : 'Evidence could not be saved safely on this device.')
      setMediaStatus('')
    } finally {
      setSaving(false)
    }
  }

  const receiveInput = (
    input: HTMLInputElement,
    kind: EvidenceKind,
  ) => {
    void receiveFiles(input.files, kind).finally(() => {
      input.value = ''
    })
  }

  const persistGeneratedEvidence = async (
    blob: Blob,
    kind: EvidenceKind,
    fileName: string,
    label: string,
  ) => {
    if (mounted.current) {
      setMediaError('')
      setSaving(true)
      setMediaStatus(`Saving ${label}…`)
    }
    try {
      if (storageMode === 'cloud' && !navigator.onLine) {
        throw new Error('Reconnect before adding media in cloud-save mode. Your typed notes remain saved.')
      }
      await addEvidence(visit, observation, blob, kind, fileName, commit, storageMode)
      if (mounted.current) setMediaStatus(`${storageMode === 'cloud' ? 'Uploaded' : 'Saved on device'} · ${label}`)
    } catch (error) {
      if (mounted.current) {
        setMediaError(error instanceof Error ? error.message : `${label} could not be saved.`)
        setMediaStatus('')
      }
    } finally {
      if (mounted.current) setSaving(false)
    }
  }

  const stopRecording = () => {
    const activeRecorder = recorder.current
    recorder.current = null
    if (activeRecorder) {
      try {
        activeRecorder.stop()
      } catch {
        stream.current?.getTracks().forEach((track) => track.stop())
        stream.current = null
      }
    }
    setRecording(false)
  }

  const toggleRecording = async () => {
    if (recording) {
      stopRecording()
      return
    }
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.current = mediaStream
      chunks.current = []
      const mediaRecorder = new MediaRecorder(mediaStream)
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size) chunks.current.push(event.data)
      }
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks.current, { type: mediaRecorder.mimeType || 'audio/webm' })
        void persistGeneratedEvidence(
          blob,
          'audio',
          `voice-note-${new Date().toISOString().replaceAll(':', '-')}.webm`,
          'voice note',
        )
        mediaStream.getTracks().forEach((track) => track.stop())
        if (stream.current === mediaStream) stream.current = null
      }
      mediaRecorder.start(1000)
      recorder.current = mediaRecorder
      setRecordingSeconds(0)
      setRecording(true)
    } catch {
      window.alert('Microphone access is unavailable. You can still type the note.')
    }
  }

  useEffect(() => {
    if (!recording) return
    const timer = window.setInterval(() => setRecordingSeconds((seconds) => seconds + 1), 1000)
    return () => window.clearInterval(timer)
  }, [recording])

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      const activeRecorder = recorder.current
      recorder.current = null
      if (activeRecorder) {
        try {
          activeRecorder.stop()
        } catch {
          // The recorder may already be stopping; its stream is still closed below.
        }
      }
      stream.current?.getTracks().forEach((track) => track.stop())
      stream.current = null
    }
  }, [])

  const recordingTime = `${String(Math.floor(recordingSeconds / 60)).padStart(2, '0')}:${String(recordingSeconds % 60).padStart(2, '0')}`
  const evidenceSummary = [
    photoCount ? `${photoCount} photo${photoCount === 1 ? '' : 's'}` : '',
    otherCount ? `${otherCount} other item${otherCount === 1 ? '' : 's'}` : '',
  ].filter(Boolean).join(' · ')

  return (
    <section className="media-capture">
      <div className="media-heading">
        <div>
          <h3>Photos and evidence</h3>
          <p>{evidenceSummary || 'Capture the evidence now; captions can be added later.'}</p>
        </div>
        {photoCount > 0 && (
          <button
            type="button"
            className="button button--small button--secondary"
            onClick={() => cameraInput.current?.click()}
            disabled={saving}
          >
            ＋ Take another photo
          </button>
        )}
      </div>
      <div className="capture-actions">
        <label className={`capture-button ${saving ? 'capture-button--disabled' : ''}`}>
          <span aria-hidden="true">＋</span>
          Camera
          <input
            ref={cameraInput}
            type="file"
            accept="image/*"
            capture="environment"
            disabled={saving}
            onChange={(event) => receiveInput(event.currentTarget, 'photo')}
          />
        </label>
        <label className={`capture-button ${saving ? 'capture-button--disabled' : ''}`}>
          <span aria-hidden="true">▧</span>
          Gallery
          <input
            type="file"
            accept="image/*"
            multiple
            disabled={saving}
            onChange={(event) => receiveInput(event.currentTarget, 'photo')}
          />
        </label>
        <label className={`capture-button ${saving ? 'capture-button--disabled' : ''}`}>
          <span aria-hidden="true">⌁</span>
          Document
          <input
            type="file"
            accept="application/pdf,image/*"
            disabled={saving}
            onChange={(event) => receiveInput(event.currentTarget, 'document')}
          />
        </label>
        <button
          type="button"
          className={`capture-button ${recording ? 'capture-button--recording' : ''}`}
          onClick={() => void toggleRecording()}
          disabled={saving}
          aria-label={recording ? `Stop voice recording at ${recordingTime}` : 'Start voice recording'}
        >
          <span aria-hidden="true">{recording ? '■' : '●'}</span>
          {recording ? `Stop ${recordingTime}` : 'Voice'}
        </button>
        <button type="button" className="capture-button" onClick={() => setDrawingMode({ type: 'sketch' })} disabled={saving}>
          <span aria-hidden="true">✎</span>
          Draw
        </button>
      </div>
      {mediaStatus && (
        <p className={`media-status ${saving ? 'media-status--saving' : 'media-status--saved'}`} role="status" aria-live="polite">
          <span aria-hidden="true">{saving ? '◌' : '✓'}</span>
          {mediaStatus}
        </p>
      )}
      {mediaError && <p className="alert alert--danger" role="alert">{mediaError}</p>}

      {drawingMode && (
        <SketchPad
          backgroundId={drawingMode.type === 'annotate' ? drawingMode.evidenceId : undefined}
          onCancel={() => setDrawingMode(null)}
          storageMode={storageMode}
          onSave={(blob) => {
            const type = drawingMode.type
            void persistGeneratedEvidence(
              blob,
              'sketch',
              `${type}-${new Date().toISOString().replaceAll(':', '-')}.png`,
              type === 'annotate' ? 'photo markup' : 'drawing',
            )
            setDrawingMode(null)
          }}
        />
      )}

      {evidence.length > 0 && (
        <div className="evidence-grid">
          {evidence.map((item) => (
            <EvidenceThumb
              key={item.id}
              evidence={item}
              onCaption={(caption) => void commit((current) => ({
                ...current,
                evidence: current.evidence.map((entry) => (
                  entry.id === item.id ? { ...entry, caption } : entry
                )),
              }))}
              onTranscript={(transcript) => void commit((current) => ({
                ...current,
                evidence: current.evidence.map((entry) => (
                  entry.id === item.id ? { ...entry, transcript } : entry
                )),
              }))}
              onAnnotate={item.kind === 'photo'
                ? () => setDrawingMode({ type: 'annotate', evidenceId: item.id })
                : undefined}
              onDelete={() => void commit((current) => ({
                ...current,
                evidence: current.evidence.map((entry) => (
                  entry.id === item.id ? { ...entry, deletedAt: nowIso() } : entry
                )),
                observations: current.observations.map((entry) => (
                  entry.id === observation.id
                    ? { ...entry, evidenceIds: entry.evidenceIds.filter((id) => id !== item.id) }
                    : entry
                )),
              }))}
              storageMode={storageMode}
            />
          ))}
        </div>
      )}
      {removedEvidence.length > 0 && (
        <details className="removed-evidence">
          <summary>{removedEvidence.length} removed evidence item{removedEvidence.length === 1 ? '' : 's'}</summary>
          {removedEvidence.map((item) => (
            <button
              type="button"
              className="text-button"
              key={item.id}
              onClick={() => void commit((current) => ({
                ...current,
                evidence: current.evidence.map((entry) => (
                  entry.id === item.id ? { ...entry, deletedAt: undefined } : entry
                )),
                observations: current.observations.map((entry) => (
                  entry.id === observation.id
                    ? { ...entry, evidenceIds: [...entry.evidenceIds, item.id] }
                    : entry
                )),
              }))}
            >
              Restore {item.fileName}
            </button>
          ))}
        </details>
      )}
    </section>
  )
}
